import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import { usePmUnclosedSprints } from '@data/hooks/sprintProducts';
import { insightsOverviewPath, productReportingPath } from '@app/constants/url';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { useOrganizationId } from '@app/utils/hooks';
import { ListCard, ListRow } from './ListCard';

// Enough to make a backlog visibly a backlog while the list still scrolls rather than paging. The header
// names the true total, so a longer one isn't hidden.
const SHOWN = 50;

// The click needs the sprint as well as the product, so both travel on the row.
type SprintRow = ListRow & { sprintId: string; productId: string };

/**
 * Sprints a PM still owes a close: ended, with time reported on the product, and no closed report.
 *
 * The first step of the reporting chain, sitting beside the step after it. Nothing downstream can happen
 * until a sprint is closed, so an unclosed sprint is time worked that cannot be sent, approved or invoiced.
 *
 * Oldest first: with a backlog, the sprint that has been owed a close for months is both the one holding up
 * an invoice and the one most likely to have been forgotten. Newest-first would bury it under the sprints a
 * manager has just finished and is already thinking about.
 */
export const SprintsToCloseCard = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const organizationId = useOrganizationId() ?? '';

  const params = useMemo(() => ({ limit: SHOWN }), []);
  const { data, isError } = usePmUnclosedSprints(organizationId, params);
  const items = useMemo(() => data?.items ?? [], [data]);

  // Straight to the sprint's report, which is where the Close button is — this card exists to be acted on,
  // so a row lands on the action rather than on the product it belongs to.
  const openSprint = (productId: string, sprintId: string): void =>
    transitionNavigate(productReportingPath(organizationId, productId, sprintId), 'forward', {
      state: { backPath: insightsOverviewPath(organizationId), backLabel: t('actions.backToInsights') },
    });

  const rows = useMemo<SprintRow[]>(() => {
    const range = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', timeZone: 'UTC' });

    return items.map(item => ({
      // The pair is the identity: one product can owe closes on several sprints, and one sprint on several
      // products.
      id: `${item.sprintId}:${item.productId}`,
      sprintId: item.sprintId,
      productId: item.productId,
      name: item.productName,
      // Half of the row's name, not a label on it: a product owing two closes appears twice, and the sprint
      // is the only thing telling the rows apart. The dates it spans are on hover rather than a second line.
      detail: t('sprintCount', { count: item.sprintNumber }),
      hint: `${range.format(new Date(item.startDate))} – ${range.format(new Date(item.endDate))}`,
    }));
  }, [items, i18n.language, t]);

  const total = data?.count ?? 0;

  return (
    <ListCard
      icon={Package}
      title={t('sprintsToClose')}
      // Only when the list is cut short: a count that merely repeats the visible rows is noise.
      meta={total > rows.length ? t('nShownOfTotal', { shown: rows.length, total }) : undefined}
      rows={rows}
      empty={t('allSprintsClosed')}
      failed={isError}
      onOpen={row => openSprint(row.productId, row.sprintId)}
    />
  );
};
